import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { Eye, CheckCircle, FileText } from "@phosphor-icons/react";

/**
 * Phase 4b-2: Public PDF Link Opened — in-app notification widget.
 * Shown on Dashboard. Polls every 60s.
 */
const PublicDocOpenedWidget = ({ user }) => {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        requester_user_id: user?.user_id || "",
        requester_role: user?.role || "",
        limit: "10",
      });
      const r = await axios.get(`${API}/public-doc-notifications?${params.toString()}`);
      const rows = r.data || [];
      setItems(rows);
      setUnread(rows.filter(x => !x.read).length);
    } catch { /* silent */ }
  }, [user?.user_id, user?.role]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const markRead = async (id) => {
    try { await axios.put(`${API}/public-doc-notifications/${id}/read`); load(); }
    catch { /* silent */ }
  };

  const markAllRead = async () => {
    const params = new URLSearchParams({ requester_user_id: user?.user_id || "", requester_role: user?.role || "" });
    try { await axios.put(`${API}/public-doc-notifications/mark-all-read?${params.toString()}`); load(); }
    catch { /* silent */ }
  };

  if (items.length === 0) return null;

  return (
    <div data-testid="public-doc-opened-widget" className="bg-white border border-slate-200 rounded-none shadow-sm mb-8">
      <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between bg-gradient-to-r from-emerald-50 to-white">
        <div className="flex items-center gap-2">
          <Eye size={16} weight="bold" className="text-emerald-700" />
          <h3 className="text-sm uppercase tracking-[0.2em] font-bold text-slate-700">Public PDF Link Opened</h3>
          {unread > 0 && <span data-testid="pdo-unread-badge" className="text-[10px] font-bold bg-emerald-600 text-white px-2 py-0.5 rounded-full tabular-nums">{unread} new</span>}
        </div>
        {unread > 0 && (
          <button data-testid="pdo-mark-all" onClick={markAllRead} className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1">
            <CheckCircle size={12} weight="bold" /> Mark all read
          </button>
        )}
      </div>
      <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
        {items.map((n, i) => (
          <button
            data-testid={`pdo-item-${i}`}
            key={n.notification_id}
            onClick={() => markRead(n.notification_id)}
            className={`w-full text-left px-5 py-3 hover:bg-slate-50 transition-colors flex items-start gap-3 ${!n.read ? "bg-emerald-50/30" : ""}`}
          >
            <FileText size={18} weight="bold" className="text-emerald-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="font-semibold text-sm text-slate-900 truncate">{n.title}</span>
                {!n.read && <span className="w-2 h-2 bg-emerald-500 rounded-full flex-shrink-0"></span>}
              </div>
              <div className="text-xs text-slate-600 truncate">
                <span className="capitalize">{(n.doc_type || "").replace(/-/g, " ")}</span> · <span className="font-mono">{n.doc_number || "—"}</span> · {n.open_count || 1} open{(n.open_count || 1) > 1 ? "s" : ""}
              </div>
              <div className="text-[10px] text-slate-400 mt-0.5">{(n.opened_at || "").slice(0, 16).replace("T", " ")} · {n.ip || ""}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default PublicDocOpenedWidget;
